<template>
  <VSkeletonLoader
    grid-list-lg
    fluid
    type="card"
    :loading="(isFirstLoad && !isDashboard) || isLoading"
  >
    <VRow
      row
      wrap
    >
      <VCol
        v-if="localPipelines.data.length === 0"
        cols="12"
      >
        <NoRecordCard 
          action-text="Create a New Form"
          :searched-text="filter.s"
          :title="`Your ${ featureName('Program', 'singularize') } is limited to invitees`"
          :description="`${ featureName('Pipelines') } allow you to control who you want to make your ${ featureName('Program', 'singularize') } available to. If you’ve created an application or registration form in the past, you can re-use it. Or create one from scratch.`"
          @clicked="$emit('addSection', sections[activeTab])"
        >
          <template #right-action>
            <VBtn 
              text
              class="text-caption font-weight-bold text-decoration-underline"
              style="color: #666666;"
              @click="isDashboard ? $emit('toggleAttach') : $emit('showAttachDialog', sections[activeTab].name)"
            >
              Or Start from an Existing Form >
            </VBtn>
          </template>
        </NoRecordCard>
      </VCol>
      <template v-else>
        <VCol
          v-if="!isTilesView"
          cols="12"
        >
          <PipelinesData
            :existing-data="localPipelines.data"
            @duplicated="(result) => localPipelines.data.unshift(result)"
            @deleted="
              (id) =>
                (localPipelines.data = localPipelines.data.filter(
                  (pipeline) => pipeline.id != id
                ))
            "
            @updated="
              (result) =>
                (localPipelines.data = localPipelines.data.map((pipeline) =>
                  pipeline.id === result.id ? result : pipeline
                ))
            "
          />
        </VCol>
        <DataViewUI
          v-else
          hide-actions
          hide-main-title
          hide-records-info
          default-view-mode="grid"
          background-color="transparent"
          :data="localPipelines"
          :md="$isMenuExpanded ? 6 : 3"
          :lg="!$isMenuExpanded && !isDashboard ? 3 : 4"
          :enable-infinite-loader="enableInfiniteLoader"
          @infiniteLoad="onInfiniteLoad"
        >
          <template
            slot="card"
            slot-scope="props"
          >
            <IndexCard
              disable-visibility
              height="240px"
              content-min-height="98px"
              card-shadow="0px 6px 16px rgba(216, 220, 240, 0.5)"
              :check-visibility="false"
              :enable-duplicate="false"
              :item="props.item"
              :item-type="tabName"
              :parent-model="model"
              :loading="props.item.loading || false"
              :hide-menu="!$isUserAdmin"
              :href="getHrefForItem(`${modelType}.${$isUserAdmin ? 'update' : 'view'}`, props.item.id)"
              :count="(model.pipelines_count - localPipelines.data.length) + 1"
              :show-count="checkShowCount(props.index)"
              @showMore="$emit('toggleTab', tabName.toLowerCase().pluralize())"
              @onClick="$bus.$emit('editSectionItem', tabName.toLowerCase(), props.item)"
              @enableItemLoading="(status) => $set(localPipelines.data[props.index], 'loading', status)"
            >
              <template #title-text>
                <VChip
                  text-color="white"
                  class="text-uppercase"
                  :color="props.item.type == 'pipeline' ? '#FF2A7A' : '#737ADF'"
                >
                  {{ pipelineType(props.item) }}
                  <VTooltip top>
                    <template #activator="{ on }">
                      <VIcon
                        v-on="on"
                        color="white"
                        class="pl-1"
                      >
                        mdi-information-outline
                      </VIcon>
                    </template>
                    <template v-if="props.item.type == 'pipeline'">
                      All registrants will join this {{ featureName("Program") }} upon submission
                    </template>
                    <template v-else>
                      Only selected applicants will join this {{ featureName("Program") }}
                    </template>
                  </VTooltip>
                </VChip>
              </template>
              <template #card-content>
                <VList>
                  <VListItem class="px-2 d-block">
                    <VListItemTitle>
                      <VRow>
                        <VCol
                          cols="10"
                          class="pa-0 text-truncate"
                        >
                          <span class="title font-weight-black">
                            {{ props.item.title }}
                          </span>
                          <VSpacer />
                        </VCol>
                        <VCol
                          cols="2"
                          class="pa-0 d-flex justify-end"
                        >
                          <Linkable :url="getFullyQualifiedUrlForItem(`${modelTypeRoute}.view`, props.item.id)" />
                        </VCol>
                      </VRow>
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </template>
              <template #actions>
                <VRow class="mt-n5">
                  <VCol cols="12">
                    <VRow>
                      <VCol
                        cols="12"
                        class="py-2 d-flex"
                        style="color: #808080;"
                      >
                        Total {{ props.item.type == "registration" ? "Registrants" : "Applications" }}
                      </VCol>
                      <VCol
                        cols="12"
                        class="d-flex"
                      >
                        <VChip
                          color="#FDBF14"
                          text-color="white"
                          :class="{
                            'caption-1': $vuetify.breakpoint.mdAndDown,
                          }"
                          :style="{
                            'font-weight': 700,
                            'border-radius': '33px 6px 6px 33px',
                          }"
                        >
                          {{ `${props.item.applications_count} ${props.item.type == "registration" ? "Registrants" : "Applicants"}` }}
                        </VChip>
                        <VChip
                          color="rgba(96, 190, 57, 0.2)"
                          text-color="#60BE39"
                          style="font-weight: 700; border-radius: 6px 33px 33px 6px;"
                          :class="{
                            'ml-1': true,
                            'caption-1': $vuetify.breakpoint.mdAndDown,
                          }"
                        >
                          {{ `${props.item.completed_count} Submitted - ${props.item.outstanding_count} In Progress` }}
                        </VChip>
                      </VCol>
                    </VRow>
                  </VCol>
                </VRow>
              </template>
            </IndexCard>
          </template>
        </DataViewUI>
      </template>
    </VRow>
  </VSkeletonLoader>
</template>
<script>
import { mapActions } from "vuex";
import ViewProgramSection from "@/mixins/ViewProgramSection";
import HasDueMixin from "@/mixins/HasDueOn";
import PipelinesData from "@/components/Organization/Pipelines/Data";
import IndexCard from "../Card";
import NoRecordCard from "./NoRecordCard";
export default {
  name: "Pipelines",
  mixins: [ViewProgramSection, HasDueMixin],
  components: {
    IndexCard,
    PipelinesData,
    NoRecordCard,
  },
  data() {
    return {
      tabName: "Pipeline",
      modelType: "pipeline",
      localPipelines: {
        data: [],
      },
    };
  },
  watch: {
    activeTab: {
      handler: function(value) {
        if(this.sections[value].name == "pipelines") {
          this.filter.s = "";
          this.initSearch();
        } else {
          this.localPipelines.data = this.model.pipelines;
        }
        this.enableInfiniteLoader = this.sections[value].name == "pipelines";
      },
      immediate: true,
    },
    model: {
      handler: function(value) {
        this.localPipelines.data = value.pipelines;
      },
      deep: true,
    },
    infiniteLoad: {
      handler: function(value) {
        if(!this.isDashboard) {
          this.localPipelines = value;
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      doGetPipelines: "pipeline/getAll",
    }),
    onGetAll(params) {
      params.perPage = 12;
      params.filter = {
        program: [this.model.id],
        strict: !this.filter.s,
      };
      params.s = this.filter.s;
      return this.doGetPipelines(params).then((result) => {
        if(this.isDashboard) {
          this.localPipelines = result;
        }
        return result;
      });
    },
    checkShowCount(key) {
      if(this.isDashboard) {
        return this.model.pipelines_count > this.limitForDashboard - 1 && key === this.localPipelines.data.length - 1;
      }
      return false;
    },
    pipelineType(item) {
      switch (item.type) {
        case "pipeline":
          return "registration";
        case "application-pipeline":
          return "application";
      
        default:
          return item.type.toLowerCase();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.metadata .v-list-item {
  min-height: 26px !important;
}
.status-chip {
  border-radius: 12px;
  position: absolute;
  bottom: 18px;
  right: 18px;
}
.metadata-section {
  height: 132px;
  background-color: #ffffff;
  border-radius: 8px;
  border: solid 0.5px #ececf1;
}
.caption-1 {
  font-size: 10px;
}
</style>
